import React, { useMemo, useState } from 'react';
import {
  Box,
  Card,
  CardContent,
  Chip,
  Grid,
  IconButton,
  LinearProgress,
  Stack,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import ArrowForwardIcon from '@mui/icons-material/ArrowForward';
import ArrowUpwardIcon from '@mui/icons-material/ArrowUpward';
import ArrowDownwardIcon from '@mui/icons-material/ArrowDownward';
import DragIndicatorIcon from '@mui/icons-material/DragIndicator';
import EditOutlinedIcon from '@mui/icons-material/EditOutlined';
import VisibilityIcon from '@mui/icons-material/Visibility';
import VisibilityOffIcon from '@mui/icons-material/VisibilityOff';
import {
  DndContext,
  DragOverlay,
  PointerSensor,
  closestCenter,
  useSensor,
  useSensors,
  useDroppable,
} from '@dnd-kit/core';
import {
  SortableContext,
  arrayMove,
  horizontalListSortingStrategy,
  useSortable,
  verticalListSortingStrategy,
} from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import { resolveMitraCardPreviewUrl } from '../../utils/mitraDefaultAssets';
import {
  applyBoardToCards,
  groupCardsBySection,
  moveCardInBoard,
  moveSection,
  relocateCard,
  relocateSection,
} from './mitraScreensBoardUtils';

const MitraCardThumb = ({ card }) => {
  const src = resolveMitraCardPreviewUrl(card);
  const [failed, setFailed] = useState(false);
  React.useEffect(() => setFailed(false), [src]);
  if (!src || failed) {
    return (
      <Stack alignItems="center" justifyContent="center" sx={{ height: '100%', color: 'grey.400', px: 1 }}>
        <Typography variant="caption" textAlign="center">
          Missing image
        </Typography>
      </Stack>
    );
  }
  return (
    <Box
      component="img"
      src={src}
      alt=""
      onError={() => setFailed(true)}
      sx={{ width: '100%', height: '100%', objectFit: 'cover', display: 'block' }}
    />
  );
};

function SortableCard({
  card,
  sectionKey,
  canMoveLeft,
  canMoveRight,
  onEdit,
  onToggleVisible,
  onMoveLeft,
  onMoveRight,
}) {
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } =
    useSortable({
      id: `card:${card.card_key}`,
      data: { type: 'card', cardKey: card.card_key, sectionKey },
    });

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.45 : card.is_visible ? 1 : 0.55,
  };

  return (
    <Grid item xs={12} sm={6} md={4} lg={3} ref={setNodeRef} style={style}>
      <Card
        variant="outlined"
        sx={{ height: '100%', borderRadius: 3, overflow: 'hidden', bgcolor: 'background.paper' }}
      >
        <Box sx={{ position: 'relative', bgcolor: '#0F172A', aspectRatio: '3 / 2' }}>
          <MitraCardThumb card={card} />
          {!card.thumbnail_url ? (
            <Chip
              label="App default"
              size="small"
              sx={{
                position: 'absolute',
                bottom: 8,
                left: 8,
                bgcolor: 'rgba(15,23,42,0.75)',
                color: 'white',
                fontWeight: 700,
                fontSize: 9,
                height: 20,
              }}
            />
          ) : null}
          {card.badge_en ? (
            <Chip
              label={card.badge_en}
              size="small"
              sx={{
                position: 'absolute',
                top: 8,
                right: 8,
                bgcolor: 'rgba(255,255,255,0.92)',
                fontWeight: 800,
                fontSize: 10,
              }}
            />
          ) : null}
        </Box>
        <CardContent sx={{ pt: 1.5, pb: '12px !important' }}>
          <Stack direction="row" justifyContent="space-between" alignItems="flex-start">
            <Box minWidth={0}>
              <Typography fontWeight={800} noWrap>
                {card.title_en}
              </Typography>
              <Typography variant="caption" color="text.secondary" noWrap display="block">
                {card.subtitle_en}
              </Typography>
              <Typography variant="caption" color="text.disabled" display="block" mt={0.5}>
                {card.card_key}
              </Typography>
            </Box>
            <Stack direction="row" spacing={0}>
              <Tooltip title="Drag card">
                <IconButton size="small" {...attributes} {...listeners} aria-label="Drag card">
                  <DragIndicatorIcon fontSize="small" />
                </IconButton>
              </Tooltip>
              <IconButton size="small" onClick={() => onEdit(card)} aria-label="Edit">
                <EditOutlinedIcon fontSize="small" />
              </IconButton>
            </Stack>
          </Stack>
          <Stack direction="row" spacing={0.5} alignItems="center" mt={1}>
            <Tooltip title={card.is_visible ? 'Visible in Mitra' : 'Hidden in Mitra'}>
              <IconButton size="small" onClick={() => onToggleVisible(card.card_key)}>
                {card.is_visible ? (
                  <VisibilityIcon fontSize="small" />
                ) : (
                  <VisibilityOffIcon fontSize="small" />
                )}
              </IconButton>
            </Tooltip>
            <Tooltip title="Move left in row">
              <span>
                <IconButton size="small" disabled={!canMoveLeft} onClick={onMoveLeft}>
                  <ArrowBackIcon fontSize="small" />
                </IconButton>
              </span>
            </Tooltip>
            <Tooltip title="Move right in row">
              <span>
                <IconButton size="small" disabled={!canMoveRight} onClick={onMoveRight}>
                  <ArrowForwardIcon fontSize="small" />
                </IconButton>
              </span>
            </Tooltip>
            <Chip
              size="small"
              label={`i18n ${card.i18n_completeness || 0}%`}
              color={card.i18n_completeness >= 100 ? 'success' : 'default'}
              variant="outlined"
              sx={{ ml: 'auto !important', height: 22, fontSize: 10 }}
            />
          </Stack>
          <LinearProgress
            variant="determinate"
            value={card.i18n_completeness || 0}
            sx={{ mt: 1, height: 4, borderRadius: 2 }}
          />
        </CardContent>
      </Card>
    </Grid>
  );
}

function SortableSectionRow({
  section,
  sectionIndex,
  sectionCount,
  cards,
  onEditSectionLabel,
  onMoveSection,
  onEditCard,
  onToggleVisible,
  onMoveCard,
  children,
}) {
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } =
    useSortable({
      id: `section:${section.section_key}`,
      data: { type: 'section', sectionKey: section.section_key, index: sectionIndex },
    });
  const { setNodeRef: setDropRef, isOver } = useDroppable({
    id: `section-drop:${section.section_key}`,
    data: { type: 'section-drop', sectionKey: section.section_key },
  });

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.55 : 1,
  };

  return (
    <Box
      ref={setNodeRef}
      style={style}
      mb={3}
      sx={{
        border: '1px solid',
        borderColor: isOver ? 'primary.main' : 'divider',
        borderRadius: 2,
        p: 1.5,
        bgcolor: isOver ? 'action.hover' : 'grey.50',
      }}
    >
      <Stack
        direction={{ xs: 'column', sm: 'row' }}
        alignItems={{ sm: 'center' }}
        spacing={1}
        mb={1.5}
      >
        <Tooltip title="Drag row">
          <IconButton size="small" {...attributes} {...listeners} aria-label="Drag row">
            <DragIndicatorIcon />
          </IconButton>
        </Tooltip>
        <TextField
          size="small"
          label="Row heading"
          value={section.label_en || ''}
          onChange={(e) => onEditSectionLabel(section.section_key, e.target.value)}
          sx={{ minWidth: 220, flex: 1, bgcolor: 'background.paper' }}
        />
        <Typography variant="caption" color="text.secondary" sx={{ minWidth: 100 }}>
          {section.section_key} · {cards.length} card(s)
        </Typography>
        <Stack direction="row" spacing={0.5}>
          <Tooltip title="Move row up">
            <span>
              <IconButton
                size="small"
                disabled={sectionIndex <= 0}
                onClick={() => onMoveSection(sectionIndex, -1)}
              >
                <ArrowUpwardIcon fontSize="small" />
              </IconButton>
            </span>
          </Tooltip>
          <Tooltip title="Move row down">
            <span>
              <IconButton
                size="small"
                disabled={sectionIndex >= sectionCount - 1}
                onClick={() => onMoveSection(sectionIndex, 1)}
              >
                <ArrowDownwardIcon fontSize="small" />
              </IconButton>
            </span>
          </Tooltip>
        </Stack>
      </Stack>
      <Box ref={setDropRef} minHeight={80}>
        {children}
      </Box>
    </Box>
  );
}

/**
 * Row/card board with up-down (rows), left-right (cards), and drag-drop.
 */
export default function MitraScreensBoard({
  zone,
  cards,
  layout,
  onCardsChange,
  onLayoutChange,
  onEditCard,
  onToggleVisible,
  /** When set, only these section_keys are shown/edited (e.g. account_settings). */
  onlySections = null,
}) {
  const [activeDrag, setActiveDrag] = useState(null);
  const sensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 6 } }),
  );

  const sectionDefs = useMemo(() => {
    const all = layout?.sections?.[zone] || [];
    if (!onlySections?.length) return all;
    const allow = new Set(onlySections);
    return all.filter((s) => allow.has(s.section_key));
  }, [layout, zone, onlySections]);

  const zoneCards = useMemo(() => {
    let list = cards.filter((c) => c.zone === zone);
    if (onlySections?.length) {
      const allow = new Set(onlySections);
      list = list.filter((c) => allow.has(c.section_key));
    }
    return list;
  }, [cards, zone, onlySections]);

  const boardRows = useMemo(
    () => groupCardsBySection(zoneCards, sectionDefs),
    [zoneCards, sectionDefs],
  );

  const commitBoard = (nextRows, nextSections = sectionDefs) => {
    const nextCards = applyBoardToCards(cards, zone, nextRows);
    onCardsChange(nextCards);
    if (nextSections !== sectionDefs) {
      const full = layout?.sections?.[zone] || [];
      let merged = nextSections;
      if (onlySections?.length) {
        const allow = new Set(onlySections);
        const others = full.filter((s) => !allow.has(s.section_key));
        merged = [...others, ...nextSections];
      }
      onLayoutChange({
        ...layout,
        sections: {
          ...layout.sections,
          [zone]: merged.map((s, i) => ({ ...s, sort_order: s.sort_order ?? i * 10 })),
        },
      });
    }
  };

  const handleMoveSection = (index, dir) => {
    const nextSections = moveSection(sectionDefs, index, dir);
    // Rebuild board order from new section order (keep cards in their sections)
    const nextRows = groupCardsBySection(zoneCards, nextSections);
    commitBoard(nextRows, nextSections);
  };

  const handleEditSectionLabel = (sectionKey, labelEn) => {
    onLayoutChange({
      ...layout,
      sections: {
        ...layout.sections,
        [zone]: sectionDefs.map((s) =>
          s.section_key === sectionKey ? { ...s, label_en: labelEn } : s,
        ),
      },
    });
  };

  const handleMoveCard = (sectionKey, cardKey, dir) => {
    const nextRows = moveCardInBoard(boardRows, sectionKey, cardKey, dir);
    commitBoard(nextRows);
  };

  const onDragStart = (event) => {
    setActiveDrag(event.active);
  };

  const onDragEnd = (event) => {
    const { active, over } = event;
    setActiveDrag(null);
    if (!over) return;

    const activeType = active.data.current?.type;
    const overId = String(over.id);

    if (activeType === 'section') {
      const fromKey = active.data.current.sectionKey;
      let toKey = over.data.current?.sectionKey;
      if (!toKey && overId.startsWith('section:')) toKey = overId.slice(8);
      if (!toKey && overId.startsWith('section-drop:')) toKey = overId.slice(13);
      if (!toKey || fromKey === toKey) return;
      const fromIndex = sectionDefs.findIndex((s) => s.section_key === fromKey);
      const toIndex = sectionDefs.findIndex((s) => s.section_key === toKey);
      if (fromIndex < 0 || toIndex < 0) return;
      const nextSections = relocateSection(sectionDefs, fromIndex, toIndex);
      commitBoard(groupCardsBySection(zoneCards, nextSections), nextSections);
      return;
    }

    if (activeType === 'card') {
      const cardKey = active.data.current.cardKey;
      const fromSection = active.data.current.sectionKey;
      let toSection = over.data.current?.sectionKey;
      if (!toSection && overId.startsWith('section-drop:')) {
        toSection = overId.slice(13);
      }
      if (!toSection && overId.startsWith('section:')) {
        toSection = overId.slice(8);
      }
      if (!toSection && overId.startsWith('card:')) {
        const overCardKey = overId.slice(5);
        const overRow = boardRows.find((r) =>
          r.cards.some((c) => c.card_key === overCardKey),
        );
        toSection = overRow?.sectionKey;
        if (!toSection) return;
        if (fromSection === toSection) {
          const row = boardRows.find((r) => r.sectionKey === fromSection);
          const oldIndex = row.cards.findIndex((c) => c.card_key === cardKey);
          const newIndex = row.cards.findIndex((c) => c.card_key === overCardKey);
          if (oldIndex < 0 || newIndex < 0 || oldIndex === newIndex) return;
          const nextRows = boardRows.map((r) => {
            if (r.sectionKey !== fromSection) return r;
            return { ...r, cards: arrayMove(r.cards, oldIndex, newIndex) };
          });
          commitBoard(nextRows);
          return;
        }
        const toRow = boardRows.find((r) => r.sectionKey === toSection);
        const toIndex = toRow.cards.findIndex((c) => c.card_key === overCardKey);
        commitBoard(relocateCard(boardRows, fromSection, cardKey, toSection, toIndex));
        return;
      }
      if (!toSection) return;
      const toRow = boardRows.find((r) => r.sectionKey === toSection);
      const toIndex = toRow ? toRow.cards.length : 0;
      commitBoard(relocateCard(boardRows, fromSection, cardKey, toSection, toIndex));
    }
  };

  const sectionIds = sectionDefs.map((s) => `section:${s.section_key}`);

  return (
    <DndContext
      sensors={sensors}
      collisionDetection={closestCenter}
      onDragStart={onDragStart}
      onDragEnd={onDragEnd}
    >
      <SortableContext items={sectionIds} strategy={verticalListSortingStrategy}>
        {sectionDefs.map((section, sectionIndex) => {
          const row = boardRows.find((r) => r.sectionKey === section.section_key) || {
            sectionKey: section.section_key,
            cards: [],
          };
          const cardIds = row.cards.map((c) => `card:${c.card_key}`);
          return (
            <SortableSectionRow
              key={section.section_key}
              section={section}
              sectionIndex={sectionIndex}
              sectionCount={sectionDefs.length}
              cards={row.cards}
              onEditSectionLabel={handleEditSectionLabel}
              onMoveSection={handleMoveSection}
              onEditCard={onEditCard}
              onToggleVisible={onToggleVisible}
              onMoveCard={handleMoveCard}
            >
              <SortableContext items={cardIds} strategy={horizontalListSortingStrategy}>
                <Grid container spacing={2}>
                  {row.cards.map((card, cardIndex) => (
                    <SortableCard
                      key={card.card_key}
                      card={card}
                      sectionKey={section.section_key}
                      canMoveLeft={cardIndex > 0}
                      canMoveRight={cardIndex < row.cards.length - 1}
                      onEdit={onEditCard}
                      onToggleVisible={onToggleVisible}
                      onMoveLeft={() =>
                        handleMoveCard(section.section_key, card.card_key, -1)
                      }
                      onMoveRight={() =>
                        handleMoveCard(section.section_key, card.card_key, 1)
                      }
                    />
                  ))}
                  {row.cards.length === 0 ? (
                    <Grid item xs={12}>
                      <Typography variant="body2" color="text.secondary" sx={{ py: 2, px: 1 }}>
                        Drop cards here (empty row)
                      </Typography>
                    </Grid>
                  ) : null}
                </Grid>
              </SortableContext>
            </SortableSectionRow>
          );
        })}
      </SortableContext>
      <DragOverlay>
        {activeDrag ? (
          <Chip
            color="primary"
            label={
              activeDrag.data.current?.type === 'section'
                ? `Row · ${activeDrag.data.current.sectionKey}`
                : `Card · ${activeDrag.data.current?.cardKey || ''}`
            }
          />
        ) : null}
      </DragOverlay>
    </DndContext>
  );
}
